import { useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, IconButton,
  Box, Typography, Chip, Grid, Divider,
  CircularProgress, Alert, Table, TableHead,
  TableRow, TableCell, TableBody, Tooltip
} from "@mui/material";
import CloseIcon            from "@mui/icons-material/Close";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon       from "@mui/icons-material/TwoWheeler";
import ReceiptIcon          from "@mui/icons-material/Receipt";
import PersonIcon           from "@mui/icons-material/Person";
import DirectionsCarIcon    from "@mui/icons-material/DirectionsCar";
import PaymentsIcon         from "@mui/icons-material/Payments";
import SwapHorizIcon        from "@mui/icons-material/SwapHoriz";
import CardGiftcardIcon     from "@mui/icons-material/CardGiftcard";
import ReceiptLongIcon      from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon   from "@mui/icons-material/AccountBalance";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import LocalOfferIcon       from "@mui/icons-material/LocalOffer";

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN");

const fmtR = (n) =>
  `₹${fmt(n)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const SALE_TYPE_STYLE = {
  CASH:      { bg: "#e8f5e9", color: "#2e7d32", label: "Cash" },
  FINANCE:   { bg: "#e3f2fd", color: "#1565c0", label: "Finance" },
  EXCHANGE:  { bg: "#fff3e0", color: "#e65100", label: "Exchange" },
  "EX+FIN":  { bg: "#ede7f6", color: "#4527a0", label: "Exch + Finance" },
  "CASH+EX": { bg: "#fce4ec", color: "#ad1457", label: "Cash + Exch" },
};

// ── Reusable sub-components ───────────────────────────────────────────────────

const SectionHeader = ({ icon, title, color = "#1b5e20" }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1.5} mt={0.5}>
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography fontWeight={800} fontSize="0.78rem" color={color}
      sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
      {title}
    </Typography>
    <Box flex={1} height="1px" bgcolor="#e0e0e0" ml={1} />
  </Box>
);

const Field = ({ label, value, mono = false, highlight = false }) => (
  <Box mb={1.2}>
    <Typography variant="caption" color="text.secondary"
      sx={{ fontSize: "0.63rem", textTransform: "uppercase", letterSpacing: 0.7, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={highlight ? 800 : 500}
      color={highlight ? "#1b5e20" : "text.primary"}
      sx={{ fontSize: mono ? "0.73rem" : "0.82rem", fontFamily: mono ? "monospace" : "inherit" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const AmountRow = ({ label, value, color, bold }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
    <Typography variant="body2" color="text.secondary" fontSize="0.78rem">{label}</Typography>
    <Typography variant="body2" fontWeight={bold ? 800 : 600} color={color || "text.primary"}
      fontSize="0.82rem">
      {fmtR(value)}
    </Typography>
  </Box>
);

const StatusChip = ({ status }) => {
  const isPaid = status === "PAID";
  return (
    <Chip
      size="small"
      icon={isPaid
        ? <CheckCircleIcon sx={{ fontSize: 12, color: "#2e7d32 !important" }} />
        : <WarningAmberIcon sx={{ fontSize: 12, color: "#e65100 !important" }} />}
      label={status || "PAID"}
      sx={{
        bgcolor: isPaid ? "#e8f5e9" : "#fff3e0",
        color:   isPaid ? "#2e7d32" : "#e65100",
border: `1px solid ${
  isPaid ? "#a5d6a7" : "#ffb74d"
}`,        fontWeight: 700, fontSize: "0.68rem"
      }}
    />
  );
};

// ── Main Dialog ───────────────────────────────────────────────────────────────
const EvStockDetailDialog = ({ open, onClose, data, loading, error }) => {
  const [tab, setTab] = useState("overview");

  // Reset tab when dialog opens with new data
  const d = data; // alias

  const tabs = [
    { id: "overview",  label: "Overview"  },
    { id: "customer",  label: "Customer"  },
    { id: "payment",   label: "Payment"   },
    { id: "extras",    label: "Extras"    },
  ];

  const saleTypeStyle = SALE_TYPE_STYLE[d?.saleType] || { bg: "#f5f5f5", color: "#555", label: d?.saleType };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)"
        }
      }}
    >
      {/* ── HEADER ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #388e3c 100%)",
          px: 3, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              bgcolor: "rgba(255,255,255,0.15)", borderRadius: 2,
              p: 0.8, display: "flex"
            }}>
              {d?.vehicleType === "E_RICKSHAW"
                ? <ElectricRickshawIcon sx={{ color: "#fff", fontSize: 24 }} />
                : <TwoWheelerIcon       sx={{ color: "#fff", fontSize: 24 }} />}
            </Box>
            <Box>
              <Typography fontWeight={800} color="#fff" fontSize="1rem">
                {d?.model || "Sale Detail"}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {d?.invoiceNo && (
                  <Typography variant="caption" color="rgba(255,255,255,0.75)" fontWeight={600}>
                    #{d.invoiceNo}
{d.invoiceSerial
  ? ` / ${d.invoiceSerial}`
  : ""}                  </Typography>
                )}
                {d?.saleType && (
                  <Chip size="small" label={saleTypeStyle.label}
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff",
                      fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
                )}
                {d?.location && (
                  <Typography variant="caption" color="rgba(255,255,255,0.6)">
                    📍 {d.location}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {d?.saleDate && (
              <Typography variant="caption" color="rgba(255,255,255,0.7)"
                sx={{ display: { xs: "none", sm: "block" } }}>
                {fmtDate(d.saleDate)}
              </Typography>
            )}
            <IconButton onClick={onClose} size="small"
              sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── TABS ── */}
        <Box sx={{ bgcolor: "#f9fbe7", borderBottom: "1px solid #e0e0e0", px: 2, display: "flex", gap: 0 }}>
          {tabs.map((t) => (
            <Box key={t.id} onClick={() => setTab(t.id)}
              sx={{
                px: 2, py: 1.2, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700,
                color: tab === t.id ? "#1b5e20" : "#888",
                borderBottom: tab === t.id ? "2.5px solid #2e7d32" : "2.5px solid transparent",
                transition: "all 0.15s",
                "&:hover": { color: "#2e7d32" }
              }}>
              {t.label}
            </Box>
          ))}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {loading && (
          <Box py={6} textAlign="center">
            <CircularProgress color="success" size={36} />
            <Typography mt={2} color="text.secondary" fontSize="0.85rem">Loading sale details…</Typography>
          </Box>
        )}

        {error && !loading && (
          <Box p={3}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {d && !loading && !error && (
          <Box p={2.5}>

            {/* ════════════════════════════════════════════════
                TAB: OVERVIEW — Vehicle + Sale Summary
            ════════════════════════════════════════════════ */}
            {tab === "overview" && (
              <Grid container spacing={2.5}>

                {/* ── Vehicle Card ── */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2, height: "100%" }}>
                    <SectionHeader icon={<DirectionsCarIcon fontSize="small" />} title="Vehicle Details" />
                    <Grid container spacing={1}>
                      <Grid item xs={6}><Field label="Type"   value={d.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"} /></Grid>
                      <Grid item xs={6}><Field label="Brand"  value={d.companyName} /></Grid>
                      <Grid item xs={6}><Field label="Model"  value={d.model} /></Grid>
                      <Grid item xs={6}><Field label="No. Plate" value={d.noPlate} /></Grid>
                      <Grid item xs={12}><Field label="Engine No."  value={d.engineNumber}  mono /></Grid>
                      <Grid item xs={12}><Field label="Serial No." value={d.chasisNumber}  mono /></Grid>
                    </Grid>
                    {d.stockDetails && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: "0.63rem", textTransform: "uppercase", letterSpacing: 0.7 }}>
                          Stock ID
                        </Typography>
                        <Typography variant="body2" fontWeight={600}
                          fontSize="0.72rem" fontFamily="monospace" color="#555">
                          {d.evStockId || "—"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                {/* ── Sale Summary Card ── */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2, height: "100%" }}>
                    <SectionHeader icon={<ReceiptIcon fontSize="small" />} title="Sale Summary" />

                    <Box sx={{ bgcolor: "#fff", borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                      <AmountRow label="ORP (On Road Price)" value={d.orp} />
                      {(d.discount > 0) && <AmountRow label="Discount" value={-d.discount} color="#c62828" />}
                      {(d.couponCashback > 0) && <AmountRow label="Coupon Cashback" value={-d.couponCashback} color="#c62828" />}
                      <Divider sx={{ my: 0.5 }} />
                      <AmountRow label="Sale Price" value={d.salePrice} color="#1b5e20" bold />
                      {(d.cashAmount > 0) && <AmountRow label="Cash Collected" value={d.cashAmount} />}
                    </Box>

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Field label="Sale Type" value={
                          <Chip size="small" label={saleTypeStyle.label}
                            sx={{ bgcolor: saleTypeStyle.bg, color: saleTypeStyle.color,
                              fontWeight: 700, fontSize: "0.7rem", mt: 0.3 }} />
                        } />
                      </Grid>
                      <Grid item xs={6}>
                        <Field label="Sale Date" value={fmtDate(d.saleDate)} />
                      </Grid>
                      {d.soldBy && (
                        <Grid item xs={12}>
                          <Field label="Sold By"
                            value={`${d.soldBy.name || "—"} (${d.soldBy.role || ""})`} />
                        </Grid>
                      )}
                      {d.remarks && (
                        <Grid item xs={12}>
                          <Field label="Remarks" value={d.remarks} />
                        </Grid>
                      )}
                    </Grid>

                    {/* GST / Tax */}
                    {d.tax && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Field label="GST Rate" value={`${d.tax.gstRate ?? 5}%`} />
                          {d.tax.cgst !== undefined && <Field label="CGST" value={fmtR(d.tax.cgst)} />}
                          {d.tax.sgst !== undefined && <Field label="SGST" value={fmtR(d.tax.sgst)} />}
                          {d.GSTIN && <Field label="GSTIN" value={d.GSTIN} mono />}
                        </Box>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* ════════════════════════════════════════════════
                TAB: CUSTOMER
            ════════════════════════════════════════════════ */}
            {tab === "customer" && (
              <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2 }}>
                <SectionHeader icon={<PersonIcon fontSize="small" />} title="Customer Details" />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}><Field label="Customer Name"   value={d.customerName} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Father's Name"   value={d.fatherName} /></Grid>

                  {/* Mobile — may be array */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontSize: "0.63rem", textTransform: "uppercase", letterSpacing: 0.7, display: "block" }}>
                      Mobile
                    </Typography>
                    {Array.isArray(d.mobile)
                      ? d.mobile.map((m, i) => (
                          <Typography key={i} variant="body2" fontWeight={500} fontSize="0.82rem">{m}</Typography>
                        ))
                      : <Typography variant="body2" fontWeight={500} fontSize="0.82rem">{d.mobile || "—"}</Typography>
                    }
                  </Grid>

                  <Grid item xs={12} sm={6}><Field label="Address"        value={d.address} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Village"        value={d.village} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Post Office"    value={d.postOffice} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Police Station" value={d.policeStation} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Pin Code"       value={d.pinCode} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Landmark"       value={d.landmark} /></Grid>
                  {d.GSTIN && <Grid item xs={12}><Field label="GSTIN" value={d.GSTIN} mono /></Grid>}
                </Grid>
              </Box>
            )}

            {/* ════════════════════════════════════════════════
                TAB: PAYMENT — Finance / Exchange / Due
            ════════════════════════════════════════════════ */}
            {tab === "payment" && (
              <Box display="flex" flexDirection="column" gap={2}>

                {/* ── Finance ── */}
                {d.finance?.companyName && (
                  <Box sx={{ bgcolor: "#e3f2fd", borderRadius: 2, p: 2 }}>
                    <SectionHeader icon={<AccountBalanceIcon fontSize="small" />} title="Finance" color="#1565c0" />
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}><Field label="Finance Company" value={d.finance.companyName} /></Grid>
                      <Grid item xs={6}  sm={4}><Field label="Loan Amount"     value={fmtR(d.finance.amount)} highlight /></Grid>
                      <Grid item xs={6}  sm={4}><Field label="Tenure"         value={
  d.finance?.months
    ? `${d.finance.months} months`
    : "—"
} /></Grid>
                    </Grid>
                  </Box>
                )}

                {/* ── Exchange ── */}
                {d.exchange?.ownerName && (
                  <Box sx={{ bgcolor: "#fff3e0", borderRadius: 2, p: 2 }}>
                    <SectionHeader icon={<SwapHorizIcon fontSize="small" />} title="Exchange Vehicle" color="#e65100" />
                    <Grid container spacing={1} mb={1}>
                      <Grid item xs={12} sm={4}><Field label="Owner"         value={d.exchange.ownerName} /></Grid>
                      <Grid item xs={12} sm={4}><Field label="Model"         value={d.exchange.model} /></Grid>
                      <Grid item xs={12} sm={4}><Field label="Reg. Number"   value={d.exchange.registrationNumber} mono /></Grid>
                      <Grid item xs={12} sm={4}><Field label="Phone"         value={d.exchange.phone} /></Grid>
                      <Grid item xs={12} sm={4}><Field label="Location"      value={d.exchange.location} /></Grid>
                    </Grid>
                    <Box sx={{ bgcolor: "#fff", borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                      <AmountRow label="Exchange Price" value={d.exchange.exchangePrice} />
                      <AmountRow label="Paid Amount"    value={d.exchange.paidAmount} color="#2e7d32" />
                      <Divider sx={{ my: 0.5 }} />
                      <AmountRow label="Remaining" value={d.exchange.remainingAmount} color="#c62828" bold />
                      <Box display="flex" justifyContent="flex-end" mt={0.5}>
                        <StatusChip status={d.exchange.status} />
                      </Box>
                    </Box>

                    {d.exchange.payments?.length > 0 && (
                      <>
                        <Typography fontWeight={700} fontSize="0.72rem" color="#e65100" mb={0.8}>
                          Payment History
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {["#", "Amount", "Date", "Name", "Phone"].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.68rem", py: 0.8 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {d.exchange.payments.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{i + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>{fmtR(p.amount)}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{fmtDate(p.date)}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{p.name || "—"}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{p.phone || "—"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </Box>
                )}

                {/* ── Due ── */}
                {d.due && (
                  <Box sx={{ bgcolor: "#fce4ec", borderRadius: 2, p: 2 }}>
                    <SectionHeader icon={<WarningAmberIcon fontSize="small" />} title="Due / Installments" color="#ad1457" />
                    <Box sx={{ bgcolor: "#fff", borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                      <AmountRow label="Total Due Amount" value={d.due.totalAmount} />
                      <AmountRow label="Paid"             value={d.due.paidAmount} color="#2e7d32" />
                      <Divider sx={{ my: 0.5 }} />
                      <AmountRow label="Remaining"        value={d.due.remainingAmount} color="#c62828" bold />
                      <Box display="flex" justifyContent="flex-end" mt={0.5}>
                        <StatusChip status={d.due.status} />
                      </Box>
                    </Box>

                    {d.due.payments?.length > 0 && (
                      <>
                        <Typography fontWeight={700} fontSize="0.72rem" color="#ad1457" mb={0.8}>
                          Payment History
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {["#", "Amount", "Date", "Note", "Remark"].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.68rem", py: 0.8 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {d.due.payments.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{i + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>{fmtR(p.amount)}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{fmtDate(p.date)}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{p.note   || "—"}</TableCell>
                                <TableCell sx={{ fontSize: "0.72rem" }}>{p.remark || "—"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </Box>
                )}

                {/* No payment sections */}
                {!d.finance?.companyName && !d.exchange?.ownerName && !d.due && (
                  <Box textAlign="center" py={4}>
                    <PaymentsIcon sx={{ fontSize: 40, color: "#bdbdbd" }} />
                    <Typography color="text.secondary" mt={1} fontSize="0.85rem">
                      No finance / exchange / due data
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* ════════════════════════════════════════════════
                TAB: EXTRAS — Expenses / Charges / Gifts / Tax
            ════════════════════════════════════════════════ */}
            {tab === "extras" && (
              <Grid container spacing={2}>

                {/* ── Expenses ── */}
                {d.expenses && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2, height: "100%" }}>
                      <SectionHeader icon={<ReceiptLongIcon fontSize="small" />} title="Expenses" />
                      {[
                        { key: "helmet",    label: "Helmet"      },
                        { key: "seatCover", label: "Seat Cover"  },
                        { key: "bikeCover", label: "Bike Cover"  },
                        { key: "transport", label: "Transport"   },
                        { key: "tcr",       label: "TCR"         },
                        { key: "regBroker", label: "Reg. Broker" },
                      ]
                        .filter(e => d.expenses[e.key] !== undefined && d.expenses[e.key] !== null)
                        .map(e => (
                          <AmountRow key={e.key} label={e.label} value={d.expenses[e.key]} />
                        ))
                      }
                      {Object.keys(d.expenses).length === 0 && (
                        <Typography color="text.secondary" fontSize="0.8rem">No expenses</Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* ── Charges ── */}
                {d.charges && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2, height: "100%" }}>
                      <SectionHeader icon={<LocalOfferIcon fontSize="small" />} title="Charges" />
                      {d.charges.rto       !== undefined && <AmountRow label="RTO"       value={d.charges.rto} />}
                      {d.charges.insurance !== undefined && <AmountRow label="Insurance" value={d.charges.insurance} />}
                    </Box>
                  </Grid>
                )}

                {/* ── Gifts ── */}
                {d.gifts?.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2 }}>
                      <SectionHeader icon={<CardGiftcardIcon fontSize="small" />} title="Gifts" />
                      {d.gifts.map((g, i) => (
                        <Box key={i} display="flex" justifyContent="space-between" py={0.4}>
                          <Typography fontSize="0.8rem">{g.name || `Gift ${i + 1}`}</Typography>
                          <Chip size="small" label={`Qty: ${g.quantity ?? 1}`}
                            sx={{ fontSize: "0.65rem", height: 20 }} />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* ── Discount / Cashback ── */}
                {(d.discount > 0 || d.couponCashback > 0) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: "#f9fbe7", borderRadius: 2, p: 2 }}>
                      <SectionHeader icon={<LocalOfferIcon fontSize="small" />} title="Discount & Cashback" />
                      {d.discount > 0      && <AmountRow label="Discount"        value={d.discount}       color="#c62828" />}
                      {d.couponCashback > 0 && <AmountRow label="Coupon Cashback" value={d.couponCashback} color="#c62828" />}
                    </Box>
                  </Grid>
                )}

              </Grid>
            )}

          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EvStockDetailDialog;
